'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "isExternal"],
  _excluded2 = ["children", "styleType", "stylePosition", "spacing"],
  _excluded3 = ["as"],
  _excluded4 = ["as"],
  _excluded5 = ["templateAreas", "gap", "rowGap", "columnGap", "column", "row", "autoFlow", "autoRows", "templateRows", "autoColumns", "templateColumns"],
  _excluded6 = ["columns", "spacingX", "spacingY", "spacing", "minChildWidth"],
  _excluded7 = ["className", "align", "decoration", "casing"],
  _excluded8 = ["spacing", "spacingX", "spacingY", "children", "justify", "direction", "align", "className", "shouldWrapChildren"],
  _excluded9 = ["className"],
  _excluded10 = ["isInline", "direction", "align", "justify", "spacing", "wrap", "children", "divider", "className", "shouldWrapChildren"],
  _excluded11 = ["area", "colSpan", "colStart", "colEnd", "rowEnd", "rowSpan", "rowStart"],
  _excluded12 = ["className"],
  _excluded13 = ["size", "centerContent"],
  _excluded14 = ["size"],
  _excluded15 = ["offsetX", "offsetY", "offset", "placement"],
  _excluded16 = ["className"],
  _excluded17 = ["isExternal", "target", "rel", "className"],
  _excluded18 = ["className"],
  _excluded19 = ["ratio", "children", "className"],
  _excluded20 = ["className"],
  _excluded21 = ["axis"],
  _excluded22 = ["className"],
  _excluded23 = ["className", "centerContent"],
  _excluded24 = ["borderLeftWidth", "borderBottomWidth", "borderTopWidth", "borderRightWidth", "borderWidth", "borderStyle", "borderColor"],
  _excluded25 = ["className", "orientation", "__css"],
  _excluded26 = ["direction", "align", "justify", "wrap", "basis", "grow", "shrink"];
import { forwardRef, useStyleConfig, omitThemingProps, chakra, useMultiStyleConfig, useTheme, getToken } from "../system";
import { cx } from "../shared-utils";
import { jsx, jsxs, Fragment as Fragment$1 } from 'react/jsx-runtime';
import { Icon } from "../icon";
import { createContext } from "../react-context";
import { getValidChildren } from "../react-children-utils";
import { mapResponsive } from "../breakpoint-utils";
import { compact } from "../object-utils";
import { useMemo, Children, cloneElement, Fragment } from 'react';

// src/link.tsx
var Link = forwardRef(function Link2(props, ref) {
  const styles = useStyleConfig("Link", props);
  const _omitThemingProps = omitThemingProps(props),
    {
      className,
      isExternal
    } = _omitThemingProps,
    rest = _objectWithoutPropertiesLoose(_omitThemingProps, _excluded);
  return /* @__PURE__ */jsx(chakra.a, Object.assign({
    target: isExternal ? "_blank" : void 0,
    rel: isExternal ? "noopener" : void 0,
    ref,
    className: cx("chakra-link", className)
  }, rest, {
    __css: styles
  }));
});
Link.displayName = "Link";

// src/list.tsx
var [ListStylesProvider, useListStyles] = createContext({
  name: `ListStylesContext`,
  errorMessage: `useListStyles returned is 'undefined'. Seems you forgot to wrap the components in "<List />" `
});
var List = forwardRef(function List2(props, ref) {
  const styles = useMultiStyleConfig("List", props);
  const _omitThemingProps2 = omitThemingProps(props),
    {
      children,
      styleType = "none",
      stylePosition,
      spacing
    } = _omitThemingProps2,
    rest = _objectWithoutPropertiesLoose(_omitThemingProps2, _excluded2);
  const validChildren = getValidChildren(children);
  const selector = "& > *:not(style) ~ *:not(style)";
  const spacingStyle = spacing ? {
    [selector]: {
      mt: spacing
    }
  } : {};
  return /* @__PURE__ */jsx(ListStylesProvider, {
    value: styles,
    children: /* @__PURE__ */jsx(chakra.ul, Object.assign({
      ref,
      listStyleType: styleType,
      listStylePosition: stylePosition,
      role: "list",
      __css: Object.assign({}, styles.container, spacingStyle)
    }, rest, {
      children: validChildren
    }))
  });
});
List.displayName = "List";
var OrderedList = forwardRef((props, ref) => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded3);
  return /* @__PURE__ */jsx(List, Object.assign({
    ref,
    as: "ol",
    styleType: "decimal",
    marginStart: "1em"
  }, rest));
});
OrderedList.displayName = "OrderedList";
var UnorderedList = forwardRef(function UnorderedList2(props, ref) {
  const rest = _objectWithoutPropertiesLoose(props, _excluded4);
  return /* @__PURE__ */jsx(List, Object.assign({
    ref,
    as: "ul",
    styleType: "initial",
    marginStart: "1em"
  }, rest));
});
UnorderedList.displayName = "UnorderedList";
var ListItem = forwardRef(function ListItem2(props, ref) {
  const styles = useListStyles();
  return /* @__PURE__ */jsx(chakra.li, Object.assign({
    ref
  }, props, {
    __css: styles.item
  }));
});
ListItem.displayName = "ListItem";
var ListIcon = forwardRef(function ListIcon2(props, ref) {
  const styles = useListStyles();
  return /* @__PURE__ */jsx(Icon, Object.assign({
    ref,
    role: "presentation"
  }, props, {
    __css: styles.icon
  }));
});
ListIcon.displayName = "ListIcon";

// src/grid.tsx
var Grid = forwardRef(function Grid2(props, ref) {
  const {
      templateAreas,
      gap,
      rowGap,
      columnGap,
      column,
      row,
      autoFlow,
      autoRows,
      templateRows,
      autoColumns,
      templateColumns
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded5);
  const styles = {
    display: "grid",
    gridTemplateAreas: templateAreas,
    gridGap: gap,
    gridRowGap: rowGap,
    gridColumnGap: columnGap,
    gridAutoColumns: autoColumns,
    gridColumn: column,
    gridRow: row,
    gridAutoFlow: autoFlow,
    gridAutoRows: autoRows,
    gridTemplateRows: templateRows,
    gridTemplateColumns: templateColumns
  };
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    ref,
    __css: styles
  }, rest));
});
Grid.displayName = "Grid";
var SimpleGrid = forwardRef(function SimpleGrid2(props, ref) {
  const {
      columns,
      spacingX,
      spacingY,
      spacing,
      minChildWidth
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded6);
  const theme = useTheme();
  const templateColumns = minChildWidth ? widthToColumns(minChildWidth, theme) : countToColumns(columns);
  return /* @__PURE__ */jsx(Grid, Object.assign({
    ref,
    gap: spacing,
    columnGap: spacingX,
    rowGap: spacingY,
    templateColumns
  }, rest));
});
SimpleGrid.displayName = "SimpleGrid";
function toPx(n) {
  return typeof n === "number" ? `${n}px` : n;
}
function widthToColumns(width, theme) {
  return mapResponsive(width, value => {
    const _value = getToken("sizes", value, toPx(value))(theme);
    return value === null ? null : `repeat(auto-fit, minmax(${_value}, 1fr))`;
  });
}
function countToColumns(count) {
  return mapResponsive(count, value => value === null ? null : `repeat(${value}, minmax(0, 1fr))`);
}

// src/spacer.tsx
var Spacer = chakra("div", {
  baseStyle: {
    flex: 1,
    justifySelf: "stretch",
    alignSelf: "stretch"
  }
});
Spacer.displayName = "Spacer";

// src/text.tsx
var Text = forwardRef(function Text2(props, ref) {
  const styles = useStyleConfig("Text", props);
  const _omitThemingProps3 = omitThemingProps(props),
    rest = _objectWithoutPropertiesLoose(_omitThemingProps3, _excluded7);
  const aliasedProps = compact({
    textAlign: props.align,
    textDecoration: props.decoration,
    textTransform: props.casing
  });
  return /* @__PURE__ */jsx(chakra.p, Object.assign({
    ref,
    className: cx("chakra-text", props.className)
  }, aliasedProps, rest, {
    __css: styles
  }));
});
Text.displayName = "Text";

// src/wrap.tsx
var Wrap = forwardRef(function Wrap2(props, ref) {
  const {
      spacing = "0.5rem",
      spacingX,
      spacingY,
      children,
      justify,
      direction,
      align,
      className,
      shouldWrapChildren
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded8);
  const _children = useMemo(() => shouldWrapChildren ? Children.map(children, (child, index) => /* @__PURE__ */jsx(WrapItem, {
    children: child
  }, index)) : children, [children, shouldWrapChildren]);
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    ref,
    className: cx("chakra-wrap", className)
  }, rest, {
    children: /* @__PURE__ */jsx(chakra.ul, {
      className: "chakra-wrap__list",
      __css: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: justify,
        alignItems: align,
        flexDirection: direction,
        listStyleType: "none",
        gap: spacing,
        columnGap: spacingX,
        rowGap: spacingY,
        padding: "0"
      },
      children: _children
    })
  }));
});
Wrap.displayName = "Wrap";
var WrapItem = forwardRef(function WrapItem2(props, ref) {
  const {
      className
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded9);
  return /* @__PURE__ */jsx(chakra.li, Object.assign({
    ref,
    __css: {
      display: "flex",
      alignItems: "flex-start"
    },
    className: cx("chakra-wrap__listitem", className)
  }, rest));
});
WrapItem.displayName = "WrapItem";

// src/stack/stack-divider.tsx
var StackDivider = props => /* @__PURE__ */jsx(chakra.div, Object.assign({
  className: "chakra-stack__divider"
}, props, {
  __css: Object.assign({}, props["__css"], {
    borderWidth: 0,
    alignSelf: "stretch",
    borderColor: "inherit",
    width: "auto",
    height: "auto"
  })
}));
StackDivider.displayName = "StackDivider";

// src/stack/stack-item.tsx
var StackItem = props => /* @__PURE__ */jsx(chakra.div, Object.assign({
  className: "chakra-stack__item"
}, props, {
  __css: Object.assign({
    display: "inline-block",
    flex: "0 0 auto",
    minWidth: 0
  }, props["__css"])
}));
StackItem.displayName = "StackItem";

// src/stack/stack.utils.tsx
function getDividerStyles(options) {
  const {
    spacing,
    direction
  } = options;
  const dividerStyles = {
    column: {
      my: spacing,
      mx: 0,
      borderLeftWidth: 0,
      borderBottomWidth: "1px"
    },
    "column-reverse": {
      my: spacing,
      mx: 0,
      borderLeftWidth: 0,
      borderBottomWidth: "1px"
    },
    row: {
      mx: spacing,
      my: 0,
      borderLeftWidth: "1px",
      borderBottomWidth: 0
    },
    "row-reverse": {
      mx: spacing,
      my: 0,
      borderLeftWidth: "1px",
      borderBottomWidth: 0
    }
  };
  return {
    "&": mapResponsive(direction, value => dividerStyles[value])
  };
}
var Stack = forwardRef((props, ref) => {
  const {
      isInline,
      direction: directionProp,
      align,
      justify,
      spacing = "0.5rem",
      wrap,
      children,
      divider,
      className,
      shouldWrapChildren
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded10);
  const direction = isInline ? "row" : directionProp != null ? directionProp : "column";
  const dividerStyle = useMemo(() => getDividerStyles({
    spacing,
    direction
  }), [spacing, direction]);
  const hasDivider = !!divider;
  const shouldUseChildren = !shouldWrapChildren && !hasDivider;
  const clones = useMemo(() => {
    const validChildren = getValidChildren(children);
    return shouldUseChildren ? validChildren : validChildren.map((child, index) => {
      const key = typeof child.key !== "undefined" ? child.key : index;
      const isLast = index + 1 === validChildren.length;
      const wrappedChild = /* @__PURE__ */jsx(StackItem, {
        children: child
      }, key);
      const _child = shouldWrapChildren ? wrappedChild : child;
      if (!hasDivider) return _child;
      const clonedDivider = /*#__PURE__*/cloneElement(divider, {
        __css: dividerStyle
      });
      const _divider = isLast ? null : clonedDivider;
      return /* @__PURE__ */jsxs(Fragment, {
        children: [_child, _divider]
      }, key);
    });
  }, [divider, dividerStyle, hasDivider, shouldUseChildren, shouldWrapChildren, children]);
  const _className = cx("chakra-stack", className);
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    ref,
    display: "flex",
    alignItems: align,
    justifyContent: justify,
    flexDirection: direction,
    flexWrap: wrap,
    gap: hasDivider ? void 0 : spacing,
    className: _className
  }, rest, {
    children: clones
  }));
});
Stack.displayName = "Stack";
var VStack = forwardRef((props, ref) => /* @__PURE__ */jsx(Stack, Object.assign({
  align: "center"
}, props, {
  direction: "column",
  ref
})));
VStack.displayName = "VStack";
var HStack = forwardRef((props, ref) => /* @__PURE__ */jsx(Stack, Object.assign({
  align: "center"
}, props, {
  direction: "row",
  ref
})));
HStack.displayName = "HStack";

// src/grid-item.tsx
function spanFn(span) {
  return mapResponsive(span, value => value === "auto" ? "auto" : `span ${value}/span ${value}`);
}
var GridItem = forwardRef(function GridItem2(props, ref) {
  const {
      area,
      colSpan,
      colStart,
      colEnd,
      rowEnd,
      rowSpan,
      rowStart
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded11);
  const styles = compact({
    gridArea: area,
    gridColumn: spanFn(colSpan),
    gridRow: spanFn(rowSpan),
    gridColumnStart: colStart,
    gridColumnEnd: colEnd,
    gridRowStart: rowStart,
    gridRowEnd: rowEnd
  });
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    ref,
    __css: styles
  }, rest));
});
GridItem.displayName = "GridItem";

// src/heading.tsx
var Heading = forwardRef(function Heading2(props, ref) {
  const styles = useStyleConfig("Heading", props);
  const _omitThemingProps4 = omitThemingProps(props),
    rest = _objectWithoutPropertiesLoose(_omitThemingProps4, _excluded12);
  return /* @__PURE__ */jsx(chakra.h2, Object.assign({
    ref,
    className: cx("chakra-heading", props.className)
  }, rest, {
    __css: styles
  }));
});
Heading.displayName = "Heading";

// src/box.tsx
var Box = chakra("div");
Box.displayName = "Box";
var Square = forwardRef(function Square2(props, ref) {
  const {
      size,
      centerContent = true
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded13);
  const styles = centerContent ? {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  } : {};
  return /* @__PURE__ */jsx(Box, Object.assign({
    ref,
    boxSize: size,
    __css: Object.assign({}, styles, {
      flexShrink: 0,
      flexGrow: 0
    })
  }, rest));
});
Square.displayName = "Square";
var Circle = forwardRef(function Circle2(props, ref) {
  const {
      size
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded14);
  return /* @__PURE__ */jsx(Square, Object.assign({
    size,
    ref,
    borderRadius: "9999px"
  }, rest));
});
Circle.displayName = "Circle";
var escapeRegexp = term => term.replace(/[|\\{}()[\]^$+*?.-]/g, char => `\\${char}`);
function buildRegex(query) {
  const _query = query.filter(text => text.length !== 0).map(text => escapeRegexp(text.trim()));
  if (!_query.length) {
    return null;
  }
  return new RegExp(`(${_query.join("|")})`, "ig");
}
function highlightWords({
  text,
  query
}) {
  const regex = buildRegex(Array.isArray(query) ? query : [query]);
  if (!regex) {
    return [{
      text,
      match: false
    }];
  }
  const result = text.split(regex).filter(Boolean);
  return result.map(str => ({
    text: str,
    match: regex.test(str)
  }));
}
function useHighlight(props) {
  const {
    text,
    query
  } = props;
  return useMemo(() => highlightWords({
    text,
    query
  }), [text, query]);
}
var Mark = forwardRef(function Mark2(props, ref) {
  const styles = useStyleConfig("Mark", props);
  const ownProps = omitThemingProps(props);
  return /* @__PURE__ */jsx(Box, Object.assign({
    ref
  }, ownProps, {
    as: "mark",
    __css: Object.assign({
      bg: "transparent",
      whiteSpace: "nowrap"
    }, styles)
  }));
});
function Highlight(props) {
  const {
    children,
    query,
    styles
  } = props;
  if (typeof children !== "string") {
    throw new Error("The children prop of Highlight must be a string");
  }
  const chunks = useHighlight({
    query,
    text: children
  });
  return /* @__PURE__ */jsx(Fragment$1, {
    children: chunks.map((chunk, index) => {
      return chunk.match ? /* @__PURE__ */jsx(Mark, {
        sx: styles,
        children: chunk.text
      }, index) : /* @__PURE__ */jsx(Fragment, {
        children: chunk.text
      }, index);
    })
  });
}

// src/indicator.tsx
var Indicator = forwardRef(function Indicator2(props, ref) {
  const {
      offsetX,
      offsetY,
      offset = "0",
      placement = "top-end"
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded15);
  const styles = useMemo(() => ({
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    insetBlockStart: mapResponsive(placement, v => {
      const [side] = v.split("-");
      const map = {
        top: offsetY != null ? offsetY : offset,
        middle: "50%",
        bottom: "auto"
      };
      return map[side];
    }),
    insetBlockEnd: mapResponsive(placement, v => {
      const [side] = v.split("-");
      const map = {
        top: "auto",
        middle: "50%",
        bottom: offsetY != null ? offsetY : offset
      };
      return map[side];
    }),
    insetStart: mapResponsive(placement, v => {
      const [, align] = v.split("-");
      const map = {
        start: offsetX != null ? offsetX : offset,
        center: "50%",
        end: "auto"
      };
      return map[align];
    }),
    insetEnd: mapResponsive(placement, v => {
      const [, align] = v.split("-");
      const map = {
        start: "auto",
        center: "50%",
        end: offsetX != null ? offsetX : offset
      };
      return map[align];
    }),
    translate: mapResponsive(placement, v => {
      const [side, align] = v.split("-");
      const mapX = {
        start: "-50%",
        center: "-50%",
        end: "50%"
      };
      const mapY = {
        top: "-50%",
        middle: "-50%",
        bottom: "50%"
      };
      return `${mapX[align]} ${mapY[side]}`;
    })
  }), [offset, offsetX, offsetY, placement]);
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    ref,
    __css: styles
  }, rest));
});

// src/kbd.tsx
var Kbd = forwardRef(function Kbd2(props, ref) {
  const styles = useStyleConfig("Kbd", props);
  const _omitThemingProps5 = omitThemingProps(props),
    {
      className
    } = _omitThemingProps5,
    rest = _objectWithoutPropertiesLoose(_omitThemingProps5, _excluded16);
  return /* @__PURE__ */jsx(chakra.kbd, Object.assign({
    ref,
    className: cx("chakra-kbd", className)
  }, rest, {
    __css: Object.assign({
      fontFamily: "mono"
    }, styles)
  }));
});
Kbd.displayName = "Kbd";

// src/link-box.tsx
var LinkOverlay = forwardRef(function LinkOverlay2(props, ref) {
  const {
      isExternal,
      target,
      rel,
      className
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded17);
  return /* @__PURE__ */jsx(chakra.a, Object.assign({}, rest, {
    ref,
    className: cx("chakra-linkbox__overlay", className),
    rel: isExternal ? "noopener noreferrer" : rel,
    target: isExternal ? "_blank" : target,
    __css: {
      position: "static",
      "&::before": {
        content: "''",
        cursor: "inherit",
        display: "block",
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 0,
        width: "100%",
        height: "100%"
      }
    }
  }));
});
var LinkBox = forwardRef(function LinkBox2(props, ref) {
  const {
      className
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded18);
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    ref,
    position: "relative"
  }, rest, {
    className: cx("chakra-linkbox", className),
    __css: {
      "a[href]:not(.chakra-linkbox__overlay), abbr[title]": {
        position: "relative",
        zIndex: 1
      }
    }
  }));
});

// src/aspect-ratio.tsx
var AspectRatio = forwardRef(function (props, ref) {
  const {
      ratio = 4 / 3,
      children,
      className
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded19);
  const child = Children.only(children);
  const _className = cx("chakra-aspect-ratio", className);
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    ref,
    position: "relative",
    className: _className,
    _before: {
      height: 0,
      content: `""`,
      display: "block",
      paddingBottom: mapResponsive(ratio, r => `${1 / r * 100}%`)
    },
    __css: {
      "& > *:not(style)": {
        overflow: "hidden",
        position: "absolute",
        top: "0",
        right: "0",
        bottom: "0",
        left: "0",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%"
      },
      "& > img, & > video": {
        objectFit: "cover"
      }
    }
  }, rest, {
    children: child
  }));
});
AspectRatio.displayName = "AspectRatio";

// src/badge.tsx
var Badge = forwardRef(function Badge2(props, ref) {
  const styles = useStyleConfig("Badge", props);
  const _omitThemingProps6 = omitThemingProps(props),
    rest = _objectWithoutPropertiesLoose(_omitThemingProps6, _excluded20);
  return /* @__PURE__ */jsx(chakra.span, Object.assign({
    ref,
    className: cx("chakra-badge", props.className)
  }, rest, {
    __css: Object.assign({
      display: "inline-block",
      whiteSpace: "nowrap",
      verticalAlign: "middle"
    }, styles)
  }));
});
Badge.displayName = "Badge";

// src/center.tsx
var Center = chakra("div", {
  baseStyle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }
});
Center.displayName = "Center";
var centerStyles = {
  horizontal: {
    insetStart: "50%",
    transform: "translateX(-50%)"
  },
  vertical: {
    top: "50%",
    transform: "translateY(-50%)"
  },
  both: {
    insetStart: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)"
  }
};
var AbsoluteCenter = forwardRef(function AbsoluteCenter2(props, ref) {
  const {
      axis = "both"
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded21);
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    ref,
    __css: centerStyles[axis]
  }, rest, {
    position: "absolute"
  }));
});

// src/code.tsx
var Code = forwardRef(function Code2(props, ref) {
  const styles = useStyleConfig("Code", props);
  const _omitThemingProps7 = omitThemingProps(props),
    rest = _objectWithoutPropertiesLoose(_omitThemingProps7, _excluded22);
  return /* @__PURE__ */jsx(chakra.code, Object.assign({
    ref,
    className: cx("chakra-code", props.className)
  }, rest, {
    __css: Object.assign({
      display: "inline-block"
    }, styles)
  }));
});
Code.displayName = "Code";

// src/container.tsx
var Container = forwardRef(function Container2(props, ref) {
  const _omitThemingProps8 = omitThemingProps(props),
    {
      className,
      centerContent
    } = _omitThemingProps8,
    rest = _objectWithoutPropertiesLoose(_omitThemingProps8, _excluded23);
  const styles = useStyleConfig("Container", props);
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    ref,
    className: cx("chakra-container", className)
  }, rest, {
    __css: Object.assign({}, styles, centerContent && {
      display: "flex",
      flexDirection: "column",
      alignItems: "center"
    })
  }));
});
Container.displayName = "Container";

// src/divider.tsx
var Divider = forwardRef(function Divider2(props, ref) {
  const _useStyleConfig = useStyleConfig("Divider", props),
    {
      borderLeftWidth,
      borderBottomWidth,
      borderTopWidth,
      borderRightWidth,
      borderWidth,
      borderStyle,
      borderColor
    } = _useStyleConfig,
    styles = _objectWithoutPropertiesLoose(_useStyleConfig, _excluded24);
  const _omitThemingProps9 = omitThemingProps(props),
    {
      className,
      orientation = "horizontal",
      __css
    } = _omitThemingProps9,
    rest = _objectWithoutPropertiesLoose(_omitThemingProps9, _excluded25);
  const dividerStyles = {
    vertical: {
      borderLeftWidth: borderLeftWidth || borderRightWidth || borderWidth || "1px",
      height: "100%"
    },
    horizontal: {
      borderBottomWidth: borderBottomWidth || borderTopWidth || borderWidth || "1px",
      width: "100%"
    }
  };
  return /* @__PURE__ */jsx(chakra.hr, Object.assign({
    ref,
    "aria-orientation": orientation
  }, rest, {
    __css: Object.assign({}, styles, {
      border: "0",
      borderColor,
      borderStyle
    }, dividerStyles[orientation], __css),
    className: cx("chakra-divider", className)
  }));
});
Divider.displayName = "Divider";

// src/flex.tsx
var Flex = forwardRef(function Flex2(props, ref) {
  const {
      direction,
      align,
      justify,
      wrap,
      basis,
      grow,
      shrink
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded26);
  const styles = {
    display: "flex",
    flexDirection: direction,
    alignItems: align,
    justifyContent: justify,
    flexWrap: wrap,
    flexBasis: basis,
    flexGrow: grow,
    flexShrink: shrink
  };
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    ref,
    __css: styles
  }, rest));
});
Flex.displayName = "Flex";
export { AbsoluteCenter, AspectRatio, Badge, Box, Center, Circle, Code, Container, Divider, Flex, Grid, GridItem, HStack, Heading, Highlight, Indicator, Kbd, Link, LinkBox, LinkOverlay, List, ListIcon, ListItem, Mark, OrderedList, SimpleGrid, Spacer, Square, Stack, StackDivider, StackItem, Text, UnorderedList, VStack, Wrap, WrapItem, useHighlight, useListStyles };